import React, { useCallback, useMemo } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  List,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { size } from "lodash"
import { useParams } from "react-router-dom"
import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { PageCard, PageCardContent } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { Carousel, CarouselItem } from "components/Carousel"
import { EmptyData } from "components/EmptyData"
import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"
import { ContentWithAside } from "layouts/kiosk-h"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { useAppUI } from "providers/ui"
import { useMobileUrl } from "hooks/useMobileUrl"
import { InfoListItem } from "components/ListItem"
import { LocationIcon, PercentIcon } from "components/icons"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import { getDurationDate } from "utils/date"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { QRCode } from "components/QRCode"
import { ImageCarouselModal } from "components/Image"
import { useEventDetail } from "hooks/event/useEventDetail"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { LeftToRightWrapper } from "components/LeftToRightWrapper"

export const EventDetailKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const params = useParams()
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const { eventId } = params
  const { event, navigateToLocation } = useEventDetail(eventId)
  const { goBack } = useAppUI()
  const { mobileUrl } = useMobileUrl()
  const { t } = useTranslation()
  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.events) || <PercentIcon />,
    [theme]
  )
  const { styleOverrides } = theme.components?.[`VuiDetailCard`] || {}
  const { galleryCarouselOption } =
    theme.components[`VuiDetailCard`]?.defaultProps || {}
  const {
    pageCard,
    pageCardContent,
    emptyDataContainer,
    card,
    infoListItem,
    cardContent,
    cardHeader,
    cardContentPrimaryText,
    cardContentSecondaryText,
    infoContainer,
    qrCodeContainer,
    qrCode,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    buttonContainer,
    button,
    cardContentTextWrapper,
    cardMediaWrapper,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    contentWrapper,
    galleryCarouselModal,
  } = styleOverrides

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )
  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const allImagesUrls = useMemo(
    () =>
      (size(event?.allImages) > 0
        ? event?.allImages?.map((v) => v?.url)
        : []) as string[],
    [event?.allImages]
  )

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  return (
    <>
      <Layout
        aside={
          <PageCard>
            <SidebarMenu AppNavigation={AppNavigation} />
          </PageCard>
        }
      >
        <PageCard sx={pageCard}>
          <PageCardContent sx={pageCardContent}>
            {!event ? (
              <Box sx={emptyDataContainer}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("No Current Events")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            ) : (
              <Card
                elevation={0}
                sx={card}
                className={!event.descriptionText ? "no-description" : ""}
              >
                <Box sx={cardMediaWrapper}>
                  {event.allImages && (
                    <Box sx={galleryCarouselWrapper}>
                      <LeftToRightWrapper>
                        <Carousel
                          options={{
                            type: "fade",
                            padding: 0,
                            gap: 8,
                            pagination: true,
                            autoplay: true,
                            perPage: 1,
                            ...galleryCarouselOption,
                          }}
                          sx={galleryCarousel}
                        >
                          {event.allImages.map(({ id, url }) => {
                            return (
                              <CarouselItem
                                key={`slide-${id}`}
                                sx={galleryCarouselItem}
                              >
                                <CardMedia
                                  component="img"
                                  sx={galleryCarouselItem}
                                  src={url as string}
                                  onClick={() => handleImage(url)}
                                />
                              </CarouselItem>
                            )
                          })}
                        </Carousel>
                      </LeftToRightWrapper>
                    </Box>
                  )}
                </Box>
                <Box sx={contentWrapper}>
                  <CardHeader title={t(event.name)} sx={cardHeader} />
                  {event.showInfo && (
                    <List sx={infoContainer}>
                      {!event.showAlways && (
                        <InfoListItem
                          sx={infoListItem}
                          icon={calendarIcon}
                          primary={getDurationDate(
                            event.startDate,
                            event.endDate
                          )}
                          secondary={event.openingText}
                          direction="ltr"
                        />
                      )}
                      {event.hasLocation && (
                        <InfoListItem
                          sx={infoListItem}
                          icon={locationIcon}
                          primary={
                            <LocationInfo
                              feature={event.feature}
                              component="span"
                            />
                          }
                          secondary={t(event.boothName)}
                        />
                      )}
                    </List>
                  )}
                  <CardContent
                    sx={cardContent}
                    className="VuiDetailCard-cardContent"
                  >
                    <Box sx={cardContentTextWrapper}>
                      <Typography
                        variant="body1"
                        gutterBottom
                        sx={cardContentPrimaryText}
                      >
                        {t("Event Detail")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={cardContentSecondaryText}
                        dangerouslySetInnerHTML={{
                          __html: event.descriptionText,
                        }}
                      ></Typography>
                    </Box>
                  </CardContent>
                  {mobileUrl && (
                    <Box sx={qrCodeContainer}>
                      <QRCode sx={qrCode} url={mobileUrl} />
                      <Stack>
                        <Typography
                          variant="subtitle2"
                          component="p"
                          sx={qrcodePrimaryText}
                        >
                          {t("Scan Here")}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="p"
                          sx={qrcodeSecondaryText}
                        >
                          {t("For Mobile")}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  <Box sx={buttonContainer}>
                    <Button
                      variant="outlined"
                      sx={button}
                      onClick={handleClickBack}
                    >
                      {t("Back")}
                    </Button>
                    {event?.hasLocation && (
                      <Button
                        variant="contained"
                        sx={button}
                        className="highlight"
                        onClick={navigateToLocation}
                      >
                        {t("View Location")}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Card>
            )}
          </PageCardContent>
        </PageCard>
      </Layout>
      <ImageCarouselModal
        className="VuiDetailCard-galleryCarouselModal--event"
        sx={galleryCarouselModal}
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={allImagesUrls}
        onClose={handleClose}
        carouselOptions={{
          rewind: false,
        }}
      />
    </>
  )
}
